import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, initMultiMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { dateRangeOverlap, maxDate, minDate } from "../../common/utils/dateUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = leave => leave.id;

export const leaveStaffSlice = createSlice({
    name: 'staffLeaves',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(leave => state.data[key(leave)] = leave);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(leave => state.data[key(leave)] = leave);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(leave => delete state.data[key(leave)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => leaveStaffSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'leaveDetails', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'leaveDetails');

const fetchLeave = aggregatingFetch(
    id => network => network.post('/api/v2/staffMemberLeaves', { id }, leaveStaffSlice.actions.set, visualizeFailure('get')),
    ids => network => network.post('/api/v2/staffMemberLeaves', { id: ids }, leaveStaffSlice.actions.set, visualizeFailure('get')),
    { primitiveKey: true }
)
const fetchLeaveInRange = aggregatingFetch (
    (start, end) => network => network.post('/api/v2/staffMemberLeaves', { start, end }, leaveStaffSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.post('/api/v2/staffMemberLeaves', { start: minDate(keys, 0), end: maxDate(keys, 1) }, leaveStaffSlice.actions.addOrUpdate, visualizeFailure('get'))
)
// #endregion

// #region selectors
const selectLeaves = state => state.staffLeaves.data;
const selectLeave = id => createSelector([selectLeaves], leaves => leaves[id]);
const selectLeavesById = ids => createSelector([selectLeaves], leaves => ids.reduce((result, id) => {
    if (leaves[id]) {
        result.push(leaves[id]);
    }
    return result;
}, []));

const selectLeaveInRange = (start, end) => createSelector([selectLeaves], leaves => Object.values(leaves).filter(l => dateRangeOverlap(l, { start, end })));
const selectApprovedLeaveInRange = (start, end) => createSelector([selectLeaves], leaves => Object.values(leaves).filter(l => l.approval === 'approved' && dateRangeOverlap(l, { start, end })));
// #endregion

const listen = {
    memberLeaveStaffCreated: (leave, { dispatch, websocket }) => {
        markFetched(websocket, 'leave', key(leave))
        dispatch(leaveStaffSlice.actions.addOrUpdate(leave))
    },
    memberLeaveStaffUpdated: (leave, { dispatch }) => dispatch(leaveStaffSlice.actions.addOrUpdate(leave)),
    memberLeaveStaffRemoved: (leave, { dispatch }) => dispatch(leaveStaffSlice.actions.remove(leave))
}

const leaveInitByID = initMapFactory(fetchLeave, listen);
registerEntityBasedFetch('leave', leaveInitByID);

const options = {
    cleanup: { root: leaveStaffSlice.name, all: state => Object.values(state.staffLeaves), removeAll: leaveStaffSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('member')
}
export const [useStaffLeave] = dataHookWithCredentialsFactory(selectLeave, leaveInitByID, options);
export const [useStaffLeaves] = dataHookWithCredentialsFactory(selectLeavesById, initMultiMapFactory(leaveInitByID, listen), { ...options, defaultValue: null });
export const [useStaffLeaveInRange] = dataHookWithCredentialsFactory(selectLeaveInRange, queryMapFactory(fetchLeaveInRange, 'id', leaveInitByID, listen), options);
export const [useStaffApprovedLeaveInRange] = dataHookWithCredentialsFactory(selectApprovedLeaveInRange, queryMapFactory(fetchLeaveInRange, 'id', leaveInitByID, listen), options);

const reducer = { name: leaveStaffSlice.name, reducer: leaveStaffSlice.reducer };
export default reducer;